import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineAllWith } from '@shared/combineAllWith';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema } from '../file-transfer-services/interfaces';
import { MesWorkTaskValidationService } from '../Validation/mesWorkTaskValidation.service';
import { IWorktask, WorktaskService } from '../worktask.service';
import { FailedParse, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'WorkTasks';

export interface IMesWorkTaskExportData {
	alias: string;
	category: string;
	terminal: string;
	name: string;
	salary: string;
	isActive: boolean;
	tags?: Record<string, string>;
}

const MesWorkTaskSchema: IFileDataSchema<IMesWorkTaskExportData> = {
	name: sheetName,
	properties: {
		alias: { type: 'string', required: true, fileKey: 'Alias' },
		name: { type: 'string', required: true, fileKey: 'Name' },
		isActive: { type: 'boolean', required: true, fileKey: 'IsActive' },
		salary: { type: 'string', required: false, fileKey: 'SalaryType' },
		category: { type: 'string', required: false, fileKey: 'Category' },
		terminal: { type: 'string', required: false, fileKey: 'Terminal' },
		tags: { type: { record: 'string' }, required: false, fileKey: 'Tags' },
	},
};

export interface MesWorkTaskImportContext {
	worktasks: IWorktask[];
}

@Injectable({
	providedIn: 'root',
})
export class MesWorkTaskTransferService implements ITransferService<IWorktask> {
	constructor(
		private WorkTaskService: WorktaskService,
		private mesWorkTaskImportContext: MesWorkTaskValidationService,
		private excelFileTransfer: ExcelFileTransfer<IMesWorkTaskExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => false;
	public getTemplate = (): void => {
		throw new Error('Work tasks offer no import template; check hasTemplate() before calling getTemplate().');
	};

	public exportData(user: IWorktask[]) {
		return from(user).pipe(
			map((task) => {
				const category = task.tags['categoryName'] ?? '';
				const salary = task.tags['salarytype'] ?? '';
				const terminal = task.tags['terminal'] ?? '';
				// delete mapped tags
				const tags = Object.fromEntries(Object.entries(task.tags).filter(([key]) => !['categoryName', 'salarytype', 'terminal'].includes(key)));

				return {
					isActive: task.isActive,
					alias: task.alias,
					category,
					terminal,
					name: task.name,
					salary,
					tags,
				};
			}),
			toArray(),
			mergeMap((data) => {
				return this.excelFileTransfer.exportData(sheetName, data, MesWorkTaskSchema);
			}),
			map(() => user), // Return the original user array after export
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	importData(file: File | File[], overwrite: boolean = false) {
		const ctx: MesWorkTaskImportContext = {
			worktasks: [],
		};

		const ImportErrors: ImportErrorTypes[] = [];

		if (!Array.isArray(file)) {
			// If a single file is provided, we convert it to an array
			file = [file];
		}

		const ob = readFiles(file, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			combineAllWith(
				this.WorkTaskService.getWorktasks().pipe(
					map((w) => {
						ctx.worktasks = w.result;
						return {};
					}),
				),
			),
			map(([user]) => user),
			map(this.mapWorkTask),
			filterMap(this.validateWorkTask(ImportErrors)),
			mergeMap(this.createOrUpdateWorkTask(ctx, overwrite, ImportErrors)),
			toArray(),
			map((users) => {
				const successfulImports = users.map((u) => ({ Id: u.entity.alias, status: u.status }) as SuccessFullImport);

				return {
					successfulImports,
					errors: ImportErrors,
				} as ImportResponse;
			}),
		);

		return ob;
	}

	importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IMesWorkTaskExportData> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, MesWorkTaskSchema, errorList).map((imported) => imported.data);

				if (errorList.length > 0) {
					// If there are errors, we throw an error with the list of errors
					errorList.forEach((e) => {
						ImportErrors.push({
							message: e.error,
							status: ImportStatus.ParsingError,
						} as FailedParse);
					});
				}

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	mapWorkTask(task: IMesWorkTaskExportData): IWorktask {
		return {
			isActive: task.isActive,
			alias: task.alias,
			tags: {
				categoryName: task.category,
				salarytype: task.salary,
				terminal: task.terminal,
				...task.tags,
			},
			name: task.name,
			id: '', // id will be assigned by the backend
		} as IWorktask;
	}

	validateWorkTask(ImportErrors: ImportErrorTypes[]) {
		return (task: IWorktask): IWorktask | undefined => {
			// Validate task
			const validationErrors = this.mesWorkTaskImportContext.validateWorkTask(task, false);
			if (Object.keys(validationErrors).length > 0) {
				ImportErrors.push({
					Id: `(${task.alias}) ${task.name}`,
					status: ImportStatus.ValidationError,
					errors: validationErrors,
				});
				return undefined;
			}

			return task;
		};
	}

	createOrUpdateWorkTask(ctx: MesWorkTaskImportContext, overwrite: boolean, ImportErrors: ImportErrorTypes[]) {
		return (task: IWorktask) => {
			const existingTask = ctx.worktasks.find((e) => e.alias === task.alias);
			if (existingTask) {
				if (!overwrite) {
					const message = this.translator.instant('mes.administration.worktasks.import.errors.exists', { alias: task.alias });
					// Do not overwrite existing task
					ImportErrors.push({
						Id: `(${task.alias}) ${task.name}`,
						status: ImportStatus.Skipped,
						message,
					});
					return of();
				}
				// preserve the id of the existing task
				task.id = existingTask.id;
				// Update existing task
				return this.WorkTaskService.update(task).pipe(
					map(() => ({
						entity: task,
						status: ImportStatus.SuccessUpdate,
					})),

					catchError((error) => {
						const message = this.translator.instant('mes.administration.worktasks.import.errors.update-failed', { alias: task.alias, msg: error.message });
						ImportErrors.push({
							Id: `(${task.alias}) ${task.name}`,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			} else {
				// Create new task
				return this.WorkTaskService.create(task).pipe(
					map(() => ({
						entity: task,
						status: ImportStatus.SuccessCreate,
					})),

					catchError((error) => {
						const message = this.translator.instant('mes.administration.worktasks.import.errors.create-failed', { alias: task.alias, msg: error.message });
						ImportErrors.push({
							Id: `(${task.alias}) ${task.name}`,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			}
		};
	}
}
